/**
 * Settings' Funds (migration 0079; N2 of the #55 plan, §B.13): a non-profit's
 * funds, which every line of its books names.
 *
 * Shown only in a business that keeps funds. Anyone signed in reads the list;
 * whoever manages the business adds, changes and archives (the API's rule, as
 * for the chart of accounts). General, the default fund, is where a line with
 * no fund goes, and is never archived or restricted.
 *
 * A FUND'S RESTRICTION IS SHOWN FIXED once it has a posted line, since
 * changing it would move every balance it holds between the two classes of net
 * assets; the server refuses it regardless. Releasing a restriction is a
 * recorded entry, which comes with the net-asset statements (N3).
 *
 * Read on its own, as Business Details is, so a failure here is said here and
 * leaves the rest of Settings working.
 */

import { useEffect, useState } from 'react';
import { ApiError, api, type Fund, type FundInput, type FundRestriction } from '../api.js';
import { useCan } from '../auth/AuthProvider.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';

const RESTRICTION: Record<FundRestriction, string> = {
  none: 'Without Donor Restrictions',
  purpose: 'Restricted to a Purpose',
  time: 'Restricted Until a Date',
  perpetual: 'Held in Perpetuity',
};

const BLANK: FundInput = { code: '', name: '', restriction: 'none', boardDesignated: false, purpose: null, releaseOn: null };

const toInput = (fund: Fund): FundInput => ({
  code: fund.code,
  name: fund.name,
  restriction: fund.restriction,
  boardDesignated: fund.boardDesignated,
  purpose: fund.purpose,
  releaseOn: fund.releaseOn,
});

/** "Restricted Until 2027-06-30", "Without Donor Restrictions, Board Designated". */
export function describeRestriction(fund: Pick<Fund, 'restriction' | 'boardDesignated' | 'releaseOn'>): string {
  if (fund.restriction === 'time' && fund.releaseOn !== null) return `Restricted Until ${fund.releaseOn}`;
  return fund.boardDesignated ? `${RESTRICTION[fund.restriction]}, Board Designated` : RESTRICTION[fund.restriction];
}

function FundForm(props: {
  initial: FundInput;
  /** Why the restriction cannot change here, or null when it may. */
  restrictionFixed: string | null;
  submitLabel: string;
  busy: boolean;
  onSubmit: (input: FundInput) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<FundInput>(props.initial);
  const set = (patch: Partial<FundInput>) => setForm((current) => ({ ...current, ...patch }));
  return (
    <div className="new-account fund-form" role="group" aria-label={props.submitLabel}>
      <div className="field-row">
        <label className="field field-short">
          <span>Code</span>
          <input
            value={form.code}
            maxLength={20}
            onChange={(event) => {
              const code = event.target.value;
              set({ code });
            }}
          />
        </label>
        <label className="field">
          <span>Name</span>
          <input
            value={form.name}
            maxLength={100}
            onChange={(event) => {
              const name = event.target.value;
              set({ name });
            }}
          />
        </label>
        <label className="field fund-restriction">
          <span>Restriction</span>
          <select
            value={form.restriction}
            disabled={props.restrictionFixed !== null}
            onChange={(event) => {
              const restriction = event.target.value as FundRestriction;
              set({
                restriction,
                boardDesignated: restriction === 'none' ? form.boardDesignated : false,
                releaseOn: restriction === 'time' ? form.releaseOn : null,
              });
            }}
          >
            {(Object.keys(RESTRICTION) as FundRestriction[]).map((key) => (
              <option key={key} value={key}>
                {RESTRICTION[key]}
              </option>
            ))}
          </select>
        </label>
        {form.restriction === 'time' && (
          <label className="field field-medium">
            <span>Release Date</span>
            <input
              type="date"
              value={form.releaseOn ?? ''}
              disabled={props.restrictionFixed !== null}
              onChange={(event) => {
                const releaseOn = event.target.value;
                set({ releaseOn: releaseOn === '' ? null : releaseOn });
              }}
            />
          </label>
        )}
        <label className="field">
          <span>Purpose</span>
          <input
            value={form.purpose ?? ''}
            maxLength={500}
            onChange={(event) => {
              const purpose = event.target.value;
              set({ purpose: purpose === '' ? null : purpose });
            }}
          />
        </label>
        {form.restriction === 'none' && (
          <label className="toggle-line">
            <input
              type="checkbox"
              checked={form.boardDesignated}
              onChange={(event) => {
                const boardDesignated = event.target.checked;
                set({ boardDesignated });
              }}
            />
            <span>Board Designated</span>
          </label>
        )}
      </div>
      {props.restrictionFixed !== null && <p className="muted fund-fixed">{props.restrictionFixed}</p>}
      <button type="button" disabled={props.busy} onClick={() => props.onSubmit(form)}>
        {props.busy ? 'Saving…' : props.submitLabel}
      </button>
      <button type="button" className="link" onClick={props.onCancel}>
        Cancel
      </button>
    </div>
  );
}

export function FundsSection() {
  const can = useCan();
  const manages = can('manage_business');
  const [funds, setFunds] = useState<Fund[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** 'new' for Add Fund, a fund's id for its edit, null for neither. */
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .listFunds()
      .then((result) => {
        if (live) setFunds(result.funds);
      })
      .catch((caught: unknown) => {
        if (live) setLoadError(caught instanceof ApiError ? caught.message : 'Could not read the funds.');
      });
    return () => {
      live = false;
    };
  }, []);

  /** One answer replaces one fund in the list; a new one joins it. */
  function replace(fund: Fund) {
    setFunds((current) => {
      const list = current ?? [];
      return list.some((candidate) => candidate.id === fund.id)
        ? list.map((candidate) => (candidate.id === fund.id ? fund : candidate))
        : [...list, fund];
    });
  }

  async function act(run: () => Promise<{ fund: Fund }>, done: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { fund } = await run();
      replace(fund);
      setEditing(null);
      setNotice(done);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save the fund.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings-section" aria-labelledby="settings-funds">
      <h2 id="settings-funds">Funds</h2>
      <p className="muted">
        Every line of the books is in a fund, and each fund balances on its own.{' '}
        <InfoNote label="What A Fund Is">
          A fund is money held for a reason: a grant for tutoring, a building campaign, the general operating money. A
          donor&apos;s restriction is to a purpose, until a date, or in perpetuity; a board designation is the
          board&apos;s own earmark on unrestricted money, and is not a restriction. A line with no fund chosen goes in
          General. When one fund pays for another, the entry records what each owes on the interfund account. A
          fund&apos;s restriction is fixed once it has a posted line; a fund that is no longer used is archived, and
          keeps its lines.
        </InfoNote>
      </p>

      {notice && !error && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {funds === null ? (
        !loadError && <p className="muted">Loading…</p>
      ) : (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Restriction</th>
                <th>Purpose</th>
                <th>Status</th>
                {manages && <th />}
              </tr>
            </thead>
            <tbody>
              {funds.map((fund) => (
                <tr key={fund.id}>
                  <td>{fund.code}</td>
                  <td>{fund.name}</td>
                  <td>{describeRestriction(fund)}</td>
                  <td>{fund.purpose ?? ''}</td>
                  <td>
                    {fund.isDefault ? (
                      <span className="tag">Default</span>
                    ) : fund.archivedAt !== null ? (
                      <span className="tag">Archived</span>
                    ) : null}
                  </td>
                  {manages && (
                    <td className="line-actions">
                      <button
                        type="button"
                        className="link"
                        aria-label={`Change ${fund.name}`}
                        onClick={() => setEditing(fund.id)}
                      >
                        Change
                      </button>
                      {!fund.isDefault &&
                        (fund.archivedAt === null ? (
                          <button
                            type="button"
                            className="link"
                            disabled={busy}
                            aria-label={`Archive ${fund.name}`}
                            onClick={() => void act(() => api.archiveFund(fund.id), `Archived ${fund.name}`)}
                          >
                            Archive
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="link"
                            disabled={busy}
                            aria-label={`Restore ${fund.name}`}
                            onClick={() => void act(() => api.restoreFund(fund.id), `Restored ${fund.name}`)}
                          >
                            Restore
                          </button>
                        ))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {manages &&
        editing !== null &&
        (() => {
          const fund = funds?.find((candidate) => candidate.id === editing);
          if (editing !== 'new' && fund === undefined) return null;
          return (
            <FundForm
              key={editing}
              initial={fund ? toInput(fund) : BLANK}
              restrictionFixed={
                fund?.isDefault
                  ? `${fund.name} is the default fund, and stays without donor restrictions.`
                  : fund?.hasPostedLines
                    ? `${fund.name} has posted lines, so its restriction is fixed.`
                    : null
              }
              submitLabel={fund ? 'Save Fund' : 'Add Fund'}
              busy={busy}
              onCancel={() => setEditing(null)}
              onSubmit={(input) =>
                void act(
                  () => (fund ? api.updateFund(fund.id, input) : api.createFund(input)),
                  fund ? `Saved ${input.name}` : `Added ${input.name}`,
                )
              }
            />
          );
        })()}
      {manages && editing === null && funds !== null && (
        <button type="button" className="link" onClick={() => setEditing('new')}>
          Add a Fund
        </button>
      )}
    </section>
  );
}
